import { z } from 'zod';
import { ClientTagDtoSchema } from './client-tags';
export const ClientTypeSchema = z.enum(['PERSONA', 'EMPRESA']);
// Formato de NIF/DNI/NIE/CIF en España (sin validar checksum):
// DNI:  8 dígitos + 1 letra
// NIE:  [XYZ] + 7 dígitos + 1 letra
// CIF:  1 letra de control + 7 dígitos + (1 dígito o 1 letra)
const NIF_REGEX = /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[A-HJNPQRSUVW][0-9]{7}[0-9A-J])$/;
const optionalString = (max = 200) => z.string().max(max).nullable().optional();
const ClientBaseSchema = z.object({
    type: ClientTypeSchema,
    // Persona física
    firstName: optionalString(100),
    lastName1: optionalString(100),
    lastName2: optionalString(100),
    // Persona jurídica
    legalName: optionalString(300),
    // Identificación fiscal.
    // La validación del formato español se hace de forma condicional a nivel de
    // objeto (ver `applyNationalIdRefinement`), porque depende de `esNacional`/`country`.
    // Aquí solo se aplica el trim; el upper-case se aplica solo al caso español en el refine.
    nationalId: z
        .string()
        .max(40)
        .nullable()
        .optional()
        .transform((v) => (v ? v.trim() : v)),
    esNacional: z.boolean().default(true),
    currency: z.string().length(3).default('EUR'),
    language: z.string().length(2).default('es'),
    vatNumber: z.string().max(40).nullable().optional(),
    email: z
        .union([z.string().email('Email no válido'), z.literal('')])
        .nullable()
        .optional(),
    phone: optionalString(30),
    mobile: optionalString(30),
    /** Sitio web. Formato libre ("www.acme.com" o con https://); la UI antepone el esquema al enlazar. */
    web: optionalString(300),
    address: optionalString(300),
    city: optionalString(100),
    postalCode: optionalString(10),
    province: optionalString(100),
    country: z.string().length(2).optional(),
    notes: z.string().max(5000).nullable().optional(),
});
/** Un cliente se considera nacional (España) cuando `esNacional === true` o `country === 'ES'`. */
function isSpanishClient(data) {
    return data.esNacional === true || data.country === 'ES';
}
/**
 * Refinamiento condicional del NIF: solo se exige el formato español
 * (NIF/DNI/NIE/CIF, en mayúsculas) cuando el cliente es nacional. Para clientes
 * internacionales se acepta cualquier cadena no vacía (el campo sigue siendo opcional).
 */
function refineNationalId(data, ctx) {
    const id = data.nationalId;
    if (!id)
        return; // opcional / nullable: sin valor no hay nada que validar
    if (isSpanishClient(data) && !NIF_REGEX.test(id.toUpperCase())) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nationalId'],
            message: 'Formato de NIF/DNI/NIE/CIF no válido para España',
        });
    }
}
/** Pasa a mayúsculas el `nationalId` solo en el caso español; el resto se deja tal cual. */
function normalizeNationalId(data) {
    if (data.nationalId && isSpanishClient(data)) {
        return { ...data, nationalId: data.nationalId.toUpperCase() };
    }
    return data;
}
export const CreateClientSchema = ClientBaseSchema.superRefine((data, ctx) => {
    if (data.type === 'PERSONA') {
        if (!data.firstName || !data.lastName1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['firstName'],
                message: 'Indica al menos nombre y primer apellido',
            });
        }
    }
    else if (data.type === 'EMPRESA') {
        if (!data.legalName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['legalName'],
                message: 'Indica la razón social',
            });
        }
    }
    refineNationalId(data, ctx);
}).transform(normalizeNationalId);
export const UpdateClientSchema = ClientBaseSchema.partial({ type: true })
    .superRefine((data, ctx) => {
    refineNationalId(data, ctx);
})
    .transform(normalizeNationalId);
/**
 * Contacto resumido para el listado de clientes (sin notas). Permite
 * desplegar el listado y ver "personas dentro del cliente" tipo HubSpot.
 */
export const ClientListContactSchema = z.object({
    id: z.string(),
    name: z.string(),
    role: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    isPrimary: z.boolean(),
});
export const ClientListItemSchema = z.object({
    id: z.string(),
    type: ClientTypeSchema,
    displayName: z.string(),
    nationalId: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    city: z.string().nullable(),
    createdAt: z.string().datetime(),
    /** Lista de contactos del cliente (orden: principal primero, luego createdAt asc). */
    contacts: z.array(ClientListContactSchema).default([]),
    /**
     * IDs de contactos que matchearon directamente con la query `q`. Si tiene
     * elementos, el cliente apareció en el resultado SOLO por sus contactos.
     * El frontend lo usa para autoexpandir la fila y resaltar la persona.
     */
    matchedContactIds: z.array(z.string()).default([]),
    /** Etiquetas de segmentación. Van también en el LISTADO para poder verlas
     *  sin abrir cada ficha, que es lo que se necesita al preparar una campaña. */
    tags: z.array(ClientTagDtoSchema).default([]),
});
/**
 * Rentabilidad agregada del cliente: CAC, LTV y CLTV.
 * Importes monetarios en céntimos; porcentajes como fracción (0.42 = 42%).
 */
export const ClientProfitabilitySchema = z.object({
    wonProjectsCount: z.number().int(),
    capTotalCents: z.number().int(),
    cacCents: z.number().int(),
    ltvCents: z.number().int(),
    executionCostTotalCents: z.number().int(),
    /** Coste de inversión: oportunidades En Inversión + valor regalado en probono
     *  ganado. Es coste puro (sin ingreso) y resta del CLTV. */
    investedCostCents: z.number().int(),
    /** Parte de investedCostCents proveniente de oportunidades En Inversión. */
    investmentCostCents: z.number().int(),
    /** Parte de investedCostCents del valor no facturable de probono ganado. */
    probonoCostCents: z.number().int(),
    cltvCents: z.number().int(),
    grossMarginPct: z.number(),
});
export const ClientDetailSchema = ClientListItemSchema.extend({
    firstName: z.string().nullable(),
    lastName1: z.string().nullable(),
    lastName2: z.string().nullable(),
    legalName: z.string().nullable(),
    mobile: z.string().nullable(),
    web: z.string().nullable(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    province: z.string().nullable(),
    country: z.string(),
    esNacional: z.boolean(),
    currency: z.string().length(3),
    language: z.string().length(2),
    vatNumber: z.string().nullable(),
    notes: z.string().nullable(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    updatedAt: z.string().datetime(),
    profitability: ClientProfitabilitySchema,
});
/** Columnas ordenables del listado de clientes. */
export const ClientSortBySchema = z.enum(['name', 'nationalId', 'city', 'createdAt']);
export const ClientListQuerySchema = z.object({
    q: z.string().max(200).optional(),
    type: ClientTypeSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    // Techo 500 (no 100) para que los selectores de cliente no trunquen en
    // silencio en despachos con muchos clientes. El default sigue en 25 (los
    // listados paginados normales no cargan de más).
    pageSize: z.coerce.number().int().min(1).max(500).default(25),
    /**
     * Filtrar por etiquetas: ids separados por coma. Devuelve quien lleve
     * CUALQUIERA de ellas, no todas — que es lo que se necesita al preparar una
     * campaña («amigos O conocidos de la feria»). Para la intersección habrá que
     * decidirlo cuando existan los segmentos de verdad.
     */
    tagIds: z.string().max(2000).optional(),
    /** Sin sortBy: relevancia si hay búsqueda, si no fecha de alta DESC. */
    sortBy: ClientSortBySchema.optional(),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
export const ClientListResponseSchema = z.object({
    items: z.array(ClientListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
/** Compone el displayName a partir de los campos de un cliente. */
export function clientDisplayName(input) {
    if (input.type === 'EMPRESA')
        return input.legalName?.trim() || '';
    return [input.firstName, input.lastName1, input.lastName2]
        .map((s) => s?.trim() ?? '')
        .filter(Boolean)
        .join(' ');
}
//# sourceMappingURL=clients.js.map